import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatNomenclatureWithSize } from '@equip/utils';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { CloudUpload, Delete, ExpandMore, InsertDriveFile } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useCompleteLossRecord } from '@/hooks/mutations/asset-loss-management';
import { useNotifications } from '@/stores/useNotificationStore';
import type { LossRecordViewRow } from '@/services/lossRecordService';
import { httpClient } from '@/utils/httpClient';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { entityService } from '@/services/EntityService';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { useAuth } from '@/hooks/useAuth';
import type { Entity } from '@/types/Entity';
import { logger } from '@equip/utils/logger';
import { getLossTypeLabel } from './lossTypeLabels';
import { usePersonLabels } from '@/hooks/usePersonLabels';

const log = logger.child({ component: 'CompleteModal' });

interface UploadedFile {
  file: File;
  base64: string;
}

const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';
// Backend supports up to 25MB files (35MB bodyLimit accommodates base64 overhead)
const MAX_FILE_SIZE_MB = 25;

function formatAssetIdentifiers(entityData: Record<string, unknown> | null): string {
  if (!entityData) return '';

  const lin = typeof entityData.lin === 'string' ? entityData.lin.trim() : '';
  const nsn = typeof entityData.nsn === 'string' ? entityData.nsn.trim() : '';
  const serialNumber =
    typeof entityData.serial_number === 'string' ? entityData.serial_number.trim() : '';

  return [lin, nsn, serialNumber].filter(Boolean).join(' • ');
}

export interface CompleteModalProps {
  open: boolean;
  record: LossRecordViewRow | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CompleteModal: React.FC<CompleteModalProps> = ({
  open,
  record,
  onClose,
  onSuccess,
}) => {
  const isViewer = useWriteGuard();
  const { user } = useAuth();
  const notifications = useNotifications();
  const person = usePersonLabels('front_window');
  const completeMutation = useCompleteLossRecord();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Use asset snapshots from entity_data (denormalized at creation time)
  // This ensures assets display even after lifecycle changes (disposed/binned)
  // and for legacy records where assets were soft-deleted.
  const assetSnapshots = record?.entity_data?.asset_snapshots ?? [];
  const assetIds = record?.entity_data?.asset_ids ?? [];

  // Fallback: fetch from API only if no snapshots exist (backwards compatibility)
  const { data: fetchedAssets = [], isLoading: assetsLoading } = useQuery({
    queryKey: ['assets-for-loss', record?.id, assetIds],
    queryFn: async () => {
      if (assetIds.length === 0) return [];
      const params = new URLSearchParams({
        entity_type: 'eq.asset',
        is_current: 'eq.true',
        id: `in.(${assetIds.join(',')})`,
        select: 'id,display_name,entity_data',
      });
      return httpClient.get<Entity[]>(`/api/v1/entities?${params}`);
    },
    // Only fetch if no snapshots available (legacy records)
    enabled: open && assetIds.length > 0 && assetSnapshots.length === 0,
  });

  // Fetch related documents (generated forms)
  const { data: documents = [], isLoading: documentsLoading } = useQuery({
    queryKey: ['documents-for-loss', record?.id],
    queryFn: async () => {
      if (!record?.id) return [];
      const params = new URLSearchParams({
        entity_type: 'eq.document',
        is_current: 'eq.true',
        'entity_data->>related_entity_id': `eq.${record.id}`,
        select: 'id,display_name,entity_data,created_at',
      });
      return httpClient.get<Entity[]>(`/api/v1/entities?${params}`);
    },
    enabled: open && !!record?.id,
  });

  // Normalize assets: prefer snapshots, fall back to fetched data.
  // Filter by asset_ids to exclude items removed via document correction (AB#29538).
  const allowedIds = new Set(assetIds);
  const assets =
    assetSnapshots.length > 0
      ? assetSnapshots
          .filter((snapshot) => allowedIds.size === 0 || allowedIds.has(snapshot.id))
          .map((snapshot) => ({
            id: snapshot.id,
            display_name: snapshot.display_name,
            entity_data: {
              lin: snapshot.lin,
              nsn: snapshot.nsn,
              serial_number: snapshot.serial_number,
            },
          }))
      : fetchedAssets.map((asset) => ({
          id: asset.id,
          display_name: asset.display_name,
          entity_data: asset.entity_data as Record<string, unknown> | null,
        }));

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setUploadedFiles([]);
      setIsUploading(false);
    }
  }, [open]);

  const isSuspense = record?.workflow_status === 'suspense';
  const isDD200 = record?.transaction_type === 'DD200';
  const typeLabel = getLossTypeLabel({
    transactionType: record?.transaction_type ?? 'DD200',
    includeContaminationMemo: record?.include_contamination_memo,
  });

  const fileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix to get just the base64
        const base64 = result.split(',')[1] ?? '';
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const newFiles: UploadedFile[] = [];

      for (const file of Array.from(files)) {
        // Validate file size
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          notifications.error(
            'File Too Large',
            `${file.name} exceeds ${MAX_FILE_SIZE_MB}MB limit.`,
          );
          continue;
        }

        // Check for duplicates
        if (uploadedFiles.some((uf) => uf.file.name === file.name)) {
          notifications.warning('Duplicate File', `${file.name} is already added.`);
          continue;
        }

        try {
          const base64 = await fileToBase64(file);
          newFiles.push({ file, base64 });
        } catch {
          notifications.error('Upload Failed', `Failed to read ${file.name}.`);
        }
      }

      if (newFiles.length > 0) {
        setUploadedFiles((prev) => [...prev, ...newFiles]);
      }
    },
    [uploadedFiles, fileToBase64, notifications],
  );

  const handleRemoveFile = useCallback((fileName: string) => {
    setUploadedFiles((prev) => prev.filter((uf) => uf.file.name !== fileName));
  }, []);

  const handleOpenDocument = useCallback(
    async (downloadUrl: string) => {
      if (downloadUrl.startsWith('data:') || downloadUrl.startsWith('blob:')) {
        window.open(downloadUrl, '_blank');
        return;
      }

      // Pre-open window synchronously during user-gesture context (before any await)
      const docWindow = preOpenWindow();
      const opened = await openDocumentWithAuth(downloadUrl, docWindow);
      if (!opened) {
        notifications.warning(
          'Unable to open document',
          'The browser blocked the document tab. Allow pop-ups and try again.',
        );
      }
    },
    [notifications],
  );

  const createDocumentEntities = useCallback(async (): Promise<string[]> => {
    const documentIds: string[] = [];

    for (const { file, base64 } of uploadedFiles) {
      const entity = await entityService.create({
        entity_type: 'document',
        display_name: file.name,
        entity_data: {
          document_type: 'uploaded-attachment',
          filename: file.name,
          content_type: file.type,
          // Store base64 content in download_url as data URI
          // Note: content is stored only once to minimize payload size
          download_url: `data:${file.type};base64,${base64}`,
          source: 'user-upload',
          file_size: file.size,
          uploaded_at: new Date().toISOString(),
          related_entity_type: 'loss_record',
          related_entity_id: record?.id,
        },
        metadata: {
          source: 'loss-record-upload',
          related_entity_type: 'loss_record',
          related_entity_id: record?.id,
        },
        status: 'available',
      });
      documentIds.push(entity.id);
    }

    return documentIds;
  }, [uploadedFiles, record?.id]);

  const handleComplete = useCallback(async () => {
    if (!record) return;

    try {
      setIsUploading(true);

      // Create document entities for uploaded files
      const uploadedDocumentIds = await createDocumentEntities();

      await completeMutation.mutateAsync({
        id: record.id,
        uploadedDocumentIds,
        performedById: user?.id ?? '',
      });

      notifications.success('Record Completed', `${typeLabel} has been marked as complete.`);

      onSuccess?.();
      onClose();
    } catch (error) {
      log.error('Failed to complete record', { error: String(error) });

      // Provide helpful message for payload too large errors
      const errorMessage = String(error).toLowerCase();
      const errorStatus = (error as { status?: number })?.status;
      const is413Error =
        errorStatus === 413 ||
        errorMessage.includes('413') ||
        errorMessage.includes('too large') ||
        errorMessage.includes('content too large') ||
        errorMessage.includes('payload too large') ||
        errorMessage.includes('request entity too large');

      if (is413Error) {
        notifications.error(
          'File Too Large',
          `The uploaded document exceeds the ${MAX_FILE_SIZE_MB}MB limit. Please try a smaller file or compress the document.`,
        );
      } else {
        notifications.error(
          'Failed to Complete',
          error instanceof Error ? error.message : 'An unexpected error occurred.',
        );
      }
    } finally {
      setIsUploading(false);
    }
  }, [
    record,
    typeLabel,
    completeMutation,
    notifications,
    onSuccess,
    onClose,
    createDocumentEntities,
  ]);

  if (!record) return null;

  const formNumber = isDD200 ? 'DD200' : 'DA7923';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="span">
          {isSuspense ? `Complete ${typeLabel}` : `View ${typeLabel}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formNumber} - {record.person_display_name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Record Summary */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {person.label}
                </Typography>
                <Typography variant="subtitle1" fontWeight="medium">
                  {record.person_display_name ?? '—'}
                </Typography>
                {record.person_identifier && (
                  <Typography variant="body2" color="text.secondary">
                    {record.person_identifier}
                  </Typography>
                )}
              </Box>
              <Chip
                size="small"
                label={isSuspense ? 'Suspense' : 'Complete'}
                color={isSuspense ? 'warning' : 'success'}
                variant={isSuspense ? 'outlined' : 'filled'}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">
                  {record.suspense_at ? new Date(record.suspense_at).toLocaleDateString() : '—'}
                </Typography>
              </Box>
              {record.completed_at && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body1">
                    {new Date(record.completed_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Asset Details */}
          <Accordion defaultExpanded={record.asset_count <= 5}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle1" fontWeight="medium">
                Assets ({record.asset_count} item{record.asset_count !== 1 ? 's' : ''})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {assetsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : assets.length > 0 ? (
                <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {assets.map((asset) => {
                    const entityData = (asset.entity_data ?? {}) as Record<string, unknown>;
                    return (
                      <ListItem key={asset.id} divider>
                        <ListItemText
                          primary={formatNomenclatureWithSize(
                            asset.display_name,
                            (entityData.size ?? null) as string | null,
                          )}
                          secondary={formatAssetIdentifiers(entityData)}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No asset details available
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {record.comments && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Comments
              </Typography>
              <Typography variant="body1">{record.comments}</Typography>
            </Box>
          )}

          {/* Generated Documents */}
          <Box>
            <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
              Related Documents
            </Typography>
            {documentsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                <CircularProgress size={20} />
              </Box>
            ) : documents.length > 0 ? (
              <List dense>
                {documents.map((doc) => {
                  const data = (doc.entity_data ?? {}) as Record<string, unknown>;
                  const downloadUrl =
                    typeof data.download_url === 'string' && data.download_url.trim().length > 0
                      ? data.download_url
                      : null;
                  return (
                    <ListItem key={doc.id}>
                      <InsertDriveFile color="primary" sx={{ mr: 1 }} />
                      <ListItemText
                        primary={
                          downloadUrl ? (
                            <Typography
                              component="button"
                              type="button"
                              onClick={() => void handleOpenDocument(downloadUrl)}
                              color="primary"
                              sx={{
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                                background: 'none',
                                border: 'none',
                                p: 0,
                                font: 'inherit',
                                textAlign: 'left',
                                cursor: 'pointer',
                              }}
                            >
                              {doc.display_name}
                            </Typography>
                          ) : (
                            <Typography color="text.secondary">{doc.display_name}</Typography>
                          )
                        }
                        secondary={new Date(doc.created_at).toLocaleDateString()}
                      />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No related documents found.
              </Typography>
            )}
          </Box>

          {/* Document Upload Section */}
          {isSuspense && !isViewer && (
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Upload Signed Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Attach signed forms and supporting documentation (PDF, JPG, PNG up to{' '}
                {MAX_FILE_SIZE_MB}MB each).
              </Typography>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files)}
              />

              {/* Upload button / drop zone */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderStyle: 'dashed',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUpload color="action" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Click to select files or drag and drop
                </Typography>
              </Paper>

              {/* Uploaded files list */}
              {uploadedFiles.length > 0 && (
                <List dense sx={{ mt: 1 }}>
                  {uploadedFiles.map(({ file }) => (
                    <ListItem key={file.name}>
                      <InsertDriveFile color="action" sx={{ mr: 1 }} />
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(1)} KB`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveFile(file.name)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* Completion Info */}
          {isSuspense && !isDD200 && (
            <Alert severity="warning">
              <Typography variant="body2">
                Completing this record will{' '}
                <strong>remove the assets from the {person.lower}'s Hand Receipt</strong>.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={completeMutation.isPending || isUploading}
        >
          {isSuspense && !isViewer ? 'Cancel' : 'Close'}
        </Button>
        {isSuspense && !isViewer && (
          <Button
            variant="contained"
            onClick={handleComplete}
            disabled={completeMutation.isPending || isUploading}
            startIcon={
              completeMutation.isPending || isUploading ? <CircularProgress size={16} /> : undefined
            }
          >
            {isUploading
              ? 'Uploading...'
              : completeMutation.isPending
                ? 'Completing...'
                : 'Complete'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
